'use client';

import React, { useState, useEffect } from 'react';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Appointment } from './types';
import {
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  DollarSign,
  MessageCircle,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  PlayCircle,
  Bell,
  Sparkles,
  RefreshCw
} from 'lucide-react';

export interface AppointmentDetailDrawerProps {
  appointment: Appointment | null;
  onClose: () => void;
  onUpdateStatus: (appointmentId: string, newStatus: string) => void;
}

export function AppointmentDetailDrawer({
  appointment,
  onClose,
  onUpdateStatus
}: AppointmentDetailDrawerProps) {
  const [updating, setUpdating] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const [runningNotifs, setRunningNotifs] = useState(false);

  useEffect(() => {
    if (appointment?.id) {
      setLoadingNotifs(true);
      fetch(`/api/cron/notifications?appointmentId=${appointment.id}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.success && data.notifications) {
            setNotifications(data.notifications);
          }
        })
        .catch(() => {})
        .finally(() => setLoadingNotifs(false));
    }
  }, [appointment?.id]);

  if (!appointment) return null;

  const handleAction = async (status: string) => {
    setUpdating(true);
    try {
      await onUpdateStatus(appointment.id, status);
      onClose();
    } catch (err) {
      console.error('Error actualizando estado:', err);
    } finally {
      setUpdating(false);
    }
  };

  const handleRunNotifications = async () => {
    setRunningNotifs(true);
    try {
      const res = await fetch('/api/cron/notifications', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        const updatedRes = await fetch(`/api/cron/notifications?appointmentId=${appointment.id}`);
        const updatedData = await updatedRes.json();
        if (updatedData.success) {
          setNotifications(updatedData.notifications);
        }
      }
    } catch (e) {
      console.warn('Error running notifications worker:', e);
    } finally {
      setRunningNotifs(false);
    }
  };

  const cleanPhone = appointment.clientPhone.replace(/[^0-9]/g, '');
  const waUrl = `https://wa.me/${cleanPhone.startsWith('54') ? cleanPhone : '549' + cleanPhone}?text=${encodeURIComponent(
    `¡Hola ${appointment.clientName}! Te escribimos desde el salón respecto a tu turno de ${appointment.serviceName} el día ${appointment.dateStr} a las ${appointment.startTime} hs.`
  )}`;

  const status = appointment.status.toLowerCase();
  const balanceRemaining = Math.max(0, (appointment.price || 0) - (appointment.depositAmount || 0));

  const getStatusBadge = () => {
    switch (status) {
      case 'completed':
        return <Badge variant="completed" withDot>Cobrado & Finalizado</Badge>;
      case 'attended':
        return <Badge variant="attended" withDot>Cliente en Sala</Badge>;
      case 'in_progress':
        return <Badge variant="primary" withDot>Atendiendo Ahora</Badge>;
      case 'cancelled':
        return <Badge variant="cancelled" withDot>Cancelado</Badge>;
      case 'no_show':
        return <Badge variant="noshow" withDot>No Asistió</Badge>;
      default:
        return <Badge variant="confirmed" withDot>Confirmado</Badge>;
    }
  };

  return (
    <SlideOverDrawer
      isOpen={Boolean(appointment)}
      onClose={onClose}
      title={`Cita #${appointment.id.slice(0, 8)}`}
      subtitle={`Programada para el ${appointment.dateStr} (${appointment.startTime} - ${appointment.endTime} hs)`}
      footer={
        <div className="flex items-center justify-between w-full">
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>

          {status !== 'completed' && status !== 'cancelled' && (
            <Button
              variant="primary"
              onClick={() => handleAction('completed')}
              isLoading={updating}
            >
              Cobrar Turno (${balanceRemaining.toLocaleString('es-AR')})
            </Button>
          )}
        </div>
      }
    >
      {/* 1. Header de Estado */}
      <div className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-200/80 rounded-2xl">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-600">Estado actual:</span>
          {getStatusBadge()}
        </div>

        {appointment.isOverturn && (
          <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">
            Sobreturno
          </span>
        )}
      </div>

      {/* 2. Tarjeta del Cliente con accesos directos */}
      <div className="p-4 bg-white border border-slate-200 rounded-2xl space-y-3 shadow-2xs">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <User className="w-4 h-4 text-[#6255F5]" />
            <span>Información del Cliente</span>
          </p>

          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/80 transition-colors"
          >
            <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
            <span>Abrir WhatsApp</span>
          </a>
        </div>

        <div>
          <h4 className="text-sm font-bold text-slate-900">{appointment.clientName}</h4>
          <p className="text-xs text-slate-600 flex items-center gap-1.5 mt-1">
            <Phone className="w-3.5 h-3.5 text-slate-400" />
            <span>{appointment.clientPhone}</span>
          </p>
          {appointment.clientEmail && (
            <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
              <Mail className="w-3.5 h-3.5 text-slate-400" />
              <span>{appointment.clientEmail}</span>
            </p>
          )}
        </div>
      </div>

      {/* 3. Tarjeta del Servicio y Profesional */}
      <div className="p-4 bg-white border border-slate-200 rounded-2xl space-y-3 shadow-2xs">
        <p className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-[#6255F5]" />
          <span>Servicio & Profesional</span>
        </p>

        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-bold text-slate-900">{appointment.serviceName}</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Duración: {appointment.duration} min ({appointment.startTime} a {appointment.endTime} hs)
            </p>
          </div>

          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl">
            <div
              style={{ backgroundColor: appointment.staffColor || '#10b981' }}
              className="w-3 h-3 rounded-full"
            />
            <span className="text-xs font-semibold text-slate-800">
              {appointment.staffName}
            </span>
          </div>
        </div>
      </div>

      {/* 4. Desglose Financiero */}
      <div className="p-4 bg-[#F4F3FF]/40 border border-[#ECEAFF] rounded-2xl space-y-2">
        <p className="text-xs font-bold text-[#6255F5] flex items-center gap-1.5">
          <DollarSign className="w-4 h-4" />
          <span>Detalle de Cobro</span>
        </p>

        <div className="space-y-1 pt-1 text-xs">
          <div className="flex justify-between text-slate-600">
            <span>Precio Total del Servicio:</span>
            <span className="font-semibold text-slate-900">
              ${(appointment.price || 0).toLocaleString('es-AR')}
            </span>
          </div>

          <div className="flex justify-between text-emerald-700 font-medium">
            <span>Seña abonada (Mercado Pago / Anticipo):</span>
            <span>-${(appointment.depositAmount || 0).toLocaleString('es-AR')}</span>
          </div>

          <div className="flex justify-between text-slate-900 font-bold pt-1.5 border-t border-[#ECEAFF]">
            <span>Saldo a Cobrar en Salón:</span>
            <span className="text-sm text-[#6255F5]">
              ${balanceRemaining.toLocaleString('es-AR')}
            </span>
          </div>
        </div>
      </div>

      {/* 5. Notas internas */}
      {appointment.notes && (
        <div className="p-3 bg-amber-50/70 border border-amber-200/80 rounded-xl text-xs space-y-1">
          <p className="font-bold text-amber-900">Observaciones del turno:</p>
          <p className="text-amber-800">{appointment.notes}</p>
        </div>
      )}

      {/* 6. Acciones Contextuales Directas */}
      <div className="space-y-2 pt-1">
        <p className="text-xs font-bold text-slate-800">Cambio rápido de estado:</p>

        <div className="grid grid-cols-2 gap-2">
          {status !== 'attended' && status !== 'completed' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleAction('attended')}
              leftIcon={<CheckCircle2 className="w-3.5 h-3.5 text-blue-600" />}
            >
              Marcar Llegó (En sala)
            </Button>
          )}

          {status !== 'in_progress' && status !== 'completed' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleAction('in_progress')}
              leftIcon={<PlayCircle className="w-3.5 h-3.5 text-[#6255F5]" />}
            >
              Atendiendo ahora
            </Button>
          )}

          {status !== 'no_show' && status !== 'completed' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleAction('no_show')}
              leftIcon={<AlertTriangle className="w-3.5 h-3.5 text-slate-500" />}
            >
              No Asistió
            </Button>
          )}

          {status !== 'cancelled' && status !== 'completed' && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleAction('cancelled')}
              leftIcon={<XCircle className="w-3.5 h-3.5 text-rose-600" />}
            >
              Cancelar Turno
            </Button>
          )}
        </div>
      </div>

      {/* 7. Cola de Notificaciones WhatsApp (Auditoría) */}
      <div className="pt-2 border-t border-slate-150 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <Bell className="w-3.5 h-3.5 text-[#6255F5]" />
            <span>Notificaciones de WhatsApp</span>
          </p>

          <button
            type="button"
            onClick={handleRunNotifications}
            disabled={runningNotifs}
            className="text-[11px] font-semibold text-[#6255F5] hover:underline flex items-center gap-1 cursor-pointer"
          >
            <RefreshCw className={`w-3 h-3 ${runningNotifs ? 'animate-spin' : ''}`} />
            <span>Procesar cola</span>
          </button>
        </div>

        {loadingNotifs ? (
          <p className="text-xs text-slate-400 italic">Cargando avisos...</p>
        ) : notifications.length === 0 ? (
          <p className="text-xs text-slate-400 italic">
            No hay recordatorios pendientes en cola para este turno.
          </p>
        ) : (
          <div className="space-y-1.5">
            {notifications.map((n) => (
              <div
                key={n.id}
                className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs flex items-center justify-between"
              >
                <div>
                  <p className="font-semibold text-slate-800">{n.type}</p>
                  <p className="text-[10px] text-slate-500">
                    Programado para: {new Date(n.scheduledFor).toLocaleString('es-AR')}
                  </p>
                </div>
                <Badge
                  variant={
                    n.status === 'SENT'
                      ? 'confirmed'
                      : n.status === 'PENDING'
                      ? 'pending'
                      : 'default'
                  }
                >
                  {n.status}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </SlideOverDrawer>
  );
}
